/**
 * Self-hosted DeWiHall proposal metadata.
 *
 * Every proposal on chain carries a `uri` pointing at a metadata JSON. All of
 * those but MIP 26 lived on Shadow Drive (shdw-drive.genesysgo.net), which shut
 * down, so those URIs now 404. Recovered copies are checked into
 * public/proposals and mapped here by proposal address; the seven proposals
 * whose metadata could not be recovered anywhere are listed in LOST_PROPOSALS
 * so the UI can render their on-chain name instead of spinning forever.
 *
 * MIP 26 is deliberately absent from both maps: its on-chain uri already points
 * at Supabase and still resolves, so it is fetched unchanged.
 */

/** Proposal address -> self-hosted metadata JSON served from public/proposals. */
export const PROPOSAL_URI_OVERRIDES: Record<string, string> = {
  // MIP #10 Staking for CatNip (text recovered, image lost)
  '5uqxJAJYWehHUjKmsjTD574eL4txBPgrPct1upZpthsK': '/proposals/mip-10.json',
  // MIP#11: Influencer Program Proposal (text recovered, image lost)
  HFGZ1zaAcmdbsWSoz2qyAEAc84j3X5MhS62T6w3BMeRd: '/proposals/mip-11.json',
  // MIP #20 DeEEP Network Miners
  DWjKX3vtzYJmiswozjUFxiRoG7gvdPqkUBWktTK7xrLR: '/proposals/mip-20.json',
  // MIP 22: Name our Hero (the "-meow" variant, the one that collected votes)
  '8Xaki68M4fZZaqsCDwvXE8az3xZNAMzJAuhJ8NgB5KNF': '/proposals/mip-22.json',
  // MIP 23: Legendary Team Giveback
  AkCs799qejzQaAmKNb3qyuaUg79E3APLME6KxAFFAkj8: '/proposals/mip-23.json',
  // MIP #24: Staking a portion of the treasury
  JAbbwebpKDaycgHY8vEsM6hS4RoHDKCN3ASp7DsQsbTr: '/proposals/mip-24.json',
  // MIP 25: Solana Seeker Purchase
  '7iqaaHnJ6mzyhHGbyCYse5qLF1SLbW5SSejwH6UYiS9Y': '/proposals/mip-25.json',
}

/**
 * Proposals whose metadata died with Shadow Drive. Value is the on-chain name,
 * kept here for reference; the UI reads the name straight off the account.
 */
export const LOST_PROPOSALS: Record<string, string> = {
  '8ZFwsRPrxVnP8zuMaEWpjjiznYDbxCYYZxDA4FUo34fC': 'Test Vote: Ice Cream',
  '8rpSyWF7QxuKQ8ryCnN2SVwYviq5JfqfXqTACDgr7EH6':
    'MIP #1: Incentivized Auction Referrals',
  G5FrHcriDhty8wAy3cUgeAHfMkN7oJt83WdMPR6CHLbS: 'MIP #3: Deployment Process',
  '4mZ3rGD7eKnr32obDaiwpjH3QszVTY1tvcJhFMuGirhZ':
    'MIP #7: DeWiCats Promo Video',
  Btfv8EQT994Z6zEBExbe1fMDMnzN2EguTbdKe16QEgMj:
    'MIP #14 Fund AirClaw Hackathon Trip',
  JBGM5CZjThU1NSSF2HM7XoKYjySVqNs3nkQiVifF9bJ4:
    'MIP #15 Sponsorship DePin Dinner',
  // The first of the two "Name our Hero" accounts; superseded by the -meow one.
  '2njrMGoLXWoH9XesNXqZ3qBRPzGJo1kTaJSuUY4h66TC': 'MIP 22: Name our Hero',
}

/** True when the proposal's metadata is known to be gone for good. */
export function isProposalMetadataLost(address?: string | null): boolean {
  return !!address && address in LOST_PROPOSALS
}

/**
 * The URL a proposal's metadata should actually be fetched from.
 *
 * Returns the self-hosted override when there is one, `undefined` for proposals
 * whose metadata is lost (so callers can skip the request entirely), and the
 * on-chain uri otherwise.
 */
export function resolveProposalUri(
  address?: string | null,
  onChainUri?: string | null,
): string | undefined {
  if (address) {
    const override = PROPOSAL_URI_OVERRIDES[address]
    if (override) return override
    if (address in LOST_PROPOSALS) return undefined
  }

  return onChainUri || undefined
}

/**
 * Point an imageUri at the current origin when it is one of ours.
 *
 * The self-hosted JSON files carry the canonical absolute URL so they stay
 * valid wherever they are read from, but in the app the same bytes are one
 * origin-relative hop away, which also keeps previews and localhost working.
 */
export function resolveProposalImageUri(
  imageUri?: string | null,
): string | undefined {
  if (!imageUri) return undefined

  const selfHosted = imageUri.match(
    /^https?:\/\/(?:www\.)?dewicats\.xyz(\/proposals\/.+)$/,
  )
  if (selfHosted) return selfHosted[1]

  return imageUri
}
